import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import FormModal from "../billing/FormModal";
import { selectCurriculumTemplates } from "../../features/training/training-slice";
import {
  createProgramme,
  fetchCurriculumTemplates,
} from "../../features/training/slice-extensions/training-slice-extension";
import { Programme } from "../../features/training/models/Programme";
import { formatDateOnly } from "../../features/training/utils/formatDateOnly";
import { selectOrganization } from "../../features/organization/organization-slice";
import { OrganizationKind } from "../../features/organization/enums/OrganizationKind";

interface NewProgrammeDialogProps {
  onClose: () => void;
  onCreated: (programme: Programme) => void;
}

const BLANK = "";

/**
 * Creates a programme: a school's own course or level plan, or a copy of a licence-class
 * template. A driving school starts on its templates and may give its own plan a class;
 * any other school starts on its own plan and never sees a licence-class field (PD-0023).
 */
const NewProgrammeDialog: React.FC<NewProgrammeDialogProps> = ({ onClose, onCreated }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const templates = useAppSelector(selectCurriculumTemplates);
  const isDrivingSchool = useAppSelector(selectOrganization)?.kind === OrganizationKind.Fahrschule;
  // null until the user picks: a driving school starts on the first template, others on their own plan.
  const [chosenCode, setChosenCode] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [licenceClass, setLicenceClass] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (templates.length === 0) dispatch(fetchCurriculumTemplates());
  }, [dispatch, templates.length]);

  const templateCode = chosenCode ?? (isDrivingSchool ? templates[0]?.code ?? BLANK : BLANK);
  const selected = templates.find((tpl) => tpl.code === templateCode) ?? null;
  const ownClass = isDrivingSchool && licenceClass.trim() !== "" ? licenceClass.trim() : null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await dispatch(
      createProgramme({
        templateCode: selected?.code ?? null,
        name: name.trim() === "" ? null : name.trim(),
        licenceClass: selected ? null : ownClass,
      })
    );
    setSubmitting(false);
    if (createProgramme.fulfilled.match(result)) {
      onCreated(result.payload);
    } else {
      setError(result.payload?.message ?? t("training.programmes.createFailed"));
    }
  };

  const ownPlanOption = <option value={BLANK}>{t("training.programmes.blank")}</option>;
  const templateGroup = templates.length > 0 && (
    <optgroup label={t("training.programmes.licenceClassTemplates")}>
      {templates.map((tpl) => (
        <option key={tpl.code} value={tpl.code}>
          {tpl.licenceClass} ·{" "}
          {t("training.programmes.templateTopics", {
            theory: tpl.theoryTopicCount,
            practical: tpl.practicalTopicCount,
          })}
        </option>
      ))}
    </optgroup>
  );

  return (
    <FormModal onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-4 lg:p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h3>{isDrivingSchool ? t("training.programmes.newFromTemplate") : t("training.programmes.newProgramme")}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.cancel", "Cancel")}
            className="text-grey-50 hover:text-grey-30 dark:text-grey-60 dark:hover:text-grey-40"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="programme_template" className="label">
              <span className="label-text">{t("training.programmes.template")}</span>
            </label>
            <select
              id="programme_template"
              className="input w-full"
              value={templateCode}
              onChange={(e) => setChosenCode(e.target.value)}
              disabled={submitting}
            >
              {isDrivingSchool ? (
                <>
                  {templateGroup}
                  {ownPlanOption}
                </>
              ) : (
                <>
                  {ownPlanOption}
                  {templateGroup}
                </>
              )}
            </select>
            {selected && (
              <>
                <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
                  {t("training.programmes.templateVersion", {
                    date: formatDateOnly(selected.templateVersion, i18n.language),
                  })}{" "}
                  · {selected.source}
                </p>
                <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">{t("training.programmes.templateHint")}</p>
              </>
            )}
          </div>
          {!selected && isDrivingSchool && (
            <div>
              <label htmlFor="programme_class" className="label">
                <span className="label-text">{t("training.programmes.licenceClassOptional")}</span>
              </label>
              <input
                id="programme_class"
                className="input w-full"
                value={licenceClass}
                maxLength={10}
                onChange={(e) => setLicenceClass(e.target.value)}
                disabled={submitting}
              />
              <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">{t("training.programmes.licenceClassHint")}</p>
            </div>
          )}
          <div>
            <label htmlFor="programme_name" className="label">
              <span className="label-text">{t("training.programmes.name")}</span>
            </label>
            <input
              id="programme_name"
              className="input w-full"
              value={name}
              maxLength={100}
              required={!selected}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
            />
            {selected && (
              <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">{t("training.programmes.nameHint")}</p>
            )}
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            {t("common.cancel", "Cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {t("training.programmes.create")}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default NewProgrammeDialog;
